import React, { type FC, useState } from 'react';
import css from './TemplatesGrid.scss';
import {
  Badge,
  Box,
  Card,
  Heading,
  Image,
  MarketingLayout,
  Tag,
  Text,
} from '@wix/design-system';
import type {
  AppTemplate,
  Tag as TemplateTag,
} from '@wix/ambassador-devcenter-apps-v1-app-template/types';
import { DataHooks } from '../../../constants/dataHooks';
import type { OnTemplateClickFunc } from './TemplatesGrid';
import { tabsByIdMapper } from '../../../utils/mappers';
import { useTranslation } from '@wix/wix-i18n-config';

type GridItemProps = {
  template: AppTemplate;
  index: number;
  onTemplateClick: OnTemplateClickFunc;
};

export const TemplateItem: FC<GridItemProps> = ({
  template,
  index,
  onTemplateClick,
}) => {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState<boolean>(false);
  const badgeType = template.developmentTools?.[0];

  return (
    <div
      role="button"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onTemplateClick(template)}
    >
      <Card
        className={css.gridCard}
        showShadow={hovered}
        dataHook={DataHooks.TemplateWrapper(template.id as string)}
      >
        <Badge
          uppercase
          size="small"
          className={css.badge}
          dataHook={DataHooks.TemplateBadge(index)}
        >
          {t(tabsByIdMapper[badgeType]?.badgeTextKey)}
        </Badge>
        <MarketingLayout
          inverted
          size="tiny"
          alignItems="stretch"
          title={
            <Heading
              ellipsis
              size="medium"
              maxLines={2}
              className={css.reduceMarginLeft}
              dataHook={DataHooks.TemplateTitle(index)}
            >
              {template?.title}
            </Heading>
          }
          description={
            <div className={css.reduceMarginLeft}>
              <Text
                ellipsis
                secondary
                size="small"
                maxLines={2}
                dataHook={DataHooks.TemplateSubtitle(index)}
              >
                {template?.subtitle}
              </Text>
            </div>
          }
          actions={
            <Box
              gap="SP1"
              flexWrap="wrap"
              maxHeight="45px"
              overflow="hidden"
              className={css.reduceMarginLeft}
            >
              {template?.tags?.map((tag: TemplateTag, tagIndex) => (
                <Tag
                  size="tiny"
                  theme="lightOutlined"
                  key={tag.title}
                  removable={false}
                  id={tagIndex.toString()}
                  dataHook={DataHooks.TemplateTag(tagIndex, index)}
                >
                  {tag.title}
                </Tag>
              ))}
            </Box>
          }
          imagePadding={false}
          image={
            <Image
              width={160}
              height={190}
              borderRadius="8px 0 0 8px"
              src={template?.thumbnail}
              className={css.templateItemImage}
              dataHook={DataHooks.TemplateGridItemThumbnailImage(index)}
            />
          }
        />
      </Card>
    </div>
  );
};
